import {
    CSSProperties,
    ChangeEvent,
    InputHTMLAttributes,
    JSX,
    Ref,
    forwardRef,
    useCallback,
} from 'react';

import { styled } from '../theme';
import { Size, Status, matchSize } from '../utility/common-props-helpers';
import CloseButton from './CloseButton';
import InputStatus from './InputStatus';
import SvgIcon from './SvgIcon';

/**
 * Utils
 */

const getHeight = matchSize({
    md: 40,
    lg: 50,
});

/**
 * Types
 */

type NativeInputProps = InputHTMLAttributes<HTMLInputElement>;

type Props = Omit<NativeInputProps, 'size' | 'value'> & {
    innerInputStyle?: CSSProperties;
    innerRef?: Ref<HTMLInputElement> | Ref<HTMLTextAreaElement>;
    error?: boolean;
    size?: Size;
    status?: Status;
    beforeIconSrc?: string;
    beforeIconColor?: string;
    as?: keyof JSX.IntrinsicElements;
    value?: NativeInputProps['value'] | null;
    isClearable?: boolean;
};

/**
 * Styled
 */

const Container = styled('div')({
    position: 'relative',
    outline: 'none',
});

const StyledIcon = styled(SvgIcon)<Props>(({ theme, beforeIconColor = 'currentColor', size }) => ({
    position: 'absolute',
    top: (getHeight(size) - 15) / 2,
    left: 15,
    svg: {
        fill: beforeIconColor,
    },
}));

const ClearButton = styled(CloseButton)({
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
});

const StyledInput = styled('input')<Props & { hasClearButton?: boolean }>(
    ({ theme, disabled, error, size, beforeIconSrc, hasClearButton }) => ({
        fontSize: '1em',
        color: theme.color.jetBlack,
        background: theme.color.secondary1,
        borderRadius: theme.borderRadius.base,
        lineHeight: matchSize({ md: '20px', lg: '30px' })(size),
        width: '100%',
        border: error ? '1px solid ' + theme.color.danger1 : 'none',
        padding: '10px 20px',
        ...(beforeIconSrc && { paddingLeft: 45 }),
        ...(hasClearButton && { paddingRight: 40 }),
        ...(disabled && {
            opacity: 0.5,
            color: theme.color.disabled,
        }),
    })
);

/**
 * Component
 */

export default forwardRef<HTMLInputElement, Props>(
    (
        {
            disabled,
            status,
            beforeIconSrc,
            beforeIconColor,
            size = 'lg',
            className,
            style,
            innerInputStyle,
            isClearable,
            value,
            onChange,
            as = 'input',
            ...props
        },
        ref
    ) => {
        const InnerInput = useCallback(StyledInput.withComponent(as), [as]);
        const isClearButtonShown = !!isClearable && !!value;
        const handleClear = useCallback(
            () =>
                onChange?.({
                    target: { value: '' },
                    currentTarget: { value: '' },
                } as ChangeEvent<HTMLInputElement>),
            [onChange]
        );
        return (
            <Container style={style}>
                {beforeIconSrc && (
                    <StyledIcon size={size} src={beforeIconSrc} beforeIconColor={beforeIconColor} />
                )}
                {status && (
                    <InputStatus
                        size={size}
                        status={status}
                        type={props.type}
                        name={props.name}
                        value={value}
                        onChange={onChange}
                    />
                )}
                <InnerInput
                    innerRef={ref}
                    className={className}
                    disabled={disabled}
                    beforeIconSrc={beforeIconSrc}
                    size={size}
                    hasClearButton={isClearButtonShown}
                    style={innerInputStyle}
                    value={value}
                    onChange={onChange}
                    {...props}
                />
                {isClearButtonShown && <ClearButton size="md" onClick={handleClear} />}
            </Container>
        );
    }
);
